import { useState, useCallback, useEffect, useRef } from "react";
import {
  uploadAsset,
  fetchAssets,
  deleteAsset as deleteAssetService,
  renameAsset as renameAssetService,
} from "../services/asset.service.js";
import { trimAsset } from "../services/asset.service.js";

// ── localStorage cache ────────────────────────────────────────────────────
const STORAGE_KEY = (animationId) => `visuallab_assets_${animationId}`;

const loadCachedAssets = (animationId) => {
  if (!animationId) return { audio: [], image: [], video: [] };
  try {
    const raw = localStorage.getItem(STORAGE_KEY(animationId));
    if (!raw) return { audio: [], image: [], video: [] };
    const parsed = JSON.parse(raw);
    // Strip stale blob URLs
    const stripBlobs = (list) =>
      (list || []).filter((a) => a.url && !a.url.startsWith("blob:"));
    return {
      audio: stripBlobs(parsed.audio),
      image: stripBlobs(parsed.image),
      video: stripBlobs(parsed.video),
    };
  } catch {
    return { audio: [], image: [], video: [] };
  }
};

const saveCachedAssets = (animationId, assets) => {
  if (!animationId) return;
  localStorage.setItem(STORAGE_KEY(animationId), JSON.stringify(assets));
};

// ── Strip extension from filename ─────────────────────────────────────────
const stripExtension = (filename = "") => {
  const lastDot = filename.lastIndexOf(".");
  return lastDot > 0 ? filename.substring(0, lastDot) : filename;
};

// ── Audio metadata helpers ────────────────────────────────────────────────
const getAudioDuration = (file) =>
  new Promise((resolve) => {
    const audio = new Audio();
    const url = URL.createObjectURL(file);
    audio.addEventListener("loadedmetadata", () => {
      resolve(audio.duration);
      URL.revokeObjectURL(url);
    });
    audio.addEventListener("error", () => {
      resolve(0);
      URL.revokeObjectURL(url);
    });
    audio.src = url;
  });

const getImageDimensions = (file) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });

const getVideoDuration = (file) =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    video.addEventListener("loadedmetadata", () => {
      resolve(video.duration);
      URL.revokeObjectURL(url);
    });
    video.addEventListener("error", () => {
      resolve(0);
      URL.revokeObjectURL(url);
    });
    video.src = url;
  });

// ── Hook ──────────────────────────────────────────────────────────────────
const safelyCloseAudioContext = async (audioCtx) => {
  if (!audioCtx || audioCtx.state === "closed") return;
  await audioCtx.close().catch(() => {});
};

const useAnimationAssets = (animationId) => {
  const [assets, setAssets] = useState(() => loadCachedAssets(animationId));
  const [uploading, setUploading] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [trimmingId, setTrimmingId] = useState(null);

  // pendingUpload: null | { file, type, suggestedName, metadata, error }
  // When set, PresetsPanel shows rename input before uploading
  const [pendingUpload, setPendingUpload] = useState(null);

  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const intervalRef = useRef(null);

  // ── Fetch from backend on mount ───────────────────────────────────────
  useEffect(() => {
    if (!animationId) return;
    let cancelled = false;
    const load = async () => {
      setLoadingAssets(true);
      try {
        const serverAssets = await fetchAssets(animationId);
        if (!cancelled) {
          setAssets(serverAssets);
          saveCachedAssets(animationId, serverAssets);
        }
      } catch {
        // Fall back to cache
      } finally {
        if (!cancelled) setLoadingAssets(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [animationId]);

  // ── Internal updater (keeps cache in sync) ────────────────────────────
  const updateAssets = useCallback(
    (updater) => {
      setAssets((prev) => {
        const next = updater(prev);
        saveCachedAssets(animationId, next);
        return next;
      });
    },
    [animationId],
  );

  // ── Stage file for naming (called before upload) ──────────────────────
  // Returns metadata so PresetsPanel can show rename input
  const stageAudioFile = useCallback(async (file) => {
    const duration = await getAudioDuration(file);
    setPendingUpload({
      file,
      type: "audio",
      suggestedName: stripExtension(file.name),
      metadata: { duration },
      error: null,
    });
  }, []);

  const stageImageFile = useCallback(async (file) => {
    const { width, height } = await getImageDimensions(file);
    setPendingUpload({
      file,
      type: "image",
      suggestedName: stripExtension(file.name),
      metadata: { width, height },
      error: null,
    });
  }, []);

  const stageVideoFile = useCallback(async (file) => {
    const duration = await getVideoDuration(file);
    setPendingUpload({
      file,
      type: "video",
      suggestedName: stripExtension(file.name),
      metadata: { duration },
      error: null,
    });
  }, []);

  // ── Confirm upload with final name ────────────────────────────────────
  // Called when user confirms name in PresetsPanel rename input
  const confirmUpload = useCallback(
    async (name) => {
      if (!pendingUpload || !animationId) return;
      const { file, type, metadata } = pendingUpload;

      setUploading(true);
      // Clear error on retry
      setPendingUpload((prev) => ({ ...prev, error: null }));

      try {
        const asset = await uploadAsset(animationId, file, {
          name,
          ...metadata,
        });

        updateAssets((prev) => ({
          ...prev,
          [type]: [asset, ...prev[type]],
        }));

        // Upload succeeded — clear pending
        setPendingUpload(null);
        return asset;
      } catch (err) {
        // Keep rename input open with error message so user can fix name
        const message =
          err?.response?.status === 409
            ? `"${name}" already exists. Choose a different name.`
            : err?.response?.status === 400
              ? "Invalid name. Use letters, numbers, spaces, hyphens or underscores only."
              : "Upload failed. Please try again.";

        setPendingUpload((prev) => ({ ...prev, error: message }));
      } finally {
        setUploading(false);
      }
    },
    [pendingUpload, animationId, updateAssets],
  );

  // ── Cancel pending upload ─────────────────────────────────────────────
  const cancelPendingUpload = useCallback(() => {
    setPendingUpload(null);
  }, []);

  // ── Delete asset ──────────────────────────────────────────────────────
  const deleteAsset = useCallback(
    async (type, id) => {
      updateAssets((prev) => ({
        ...prev,
        [type]: prev[type].filter((a) => a.assetId !== id),
      }));
      try {
        await deleteAssetService(animationId, id);
      } catch {
        try {
          const serverAssets = await fetchAssets(animationId);
          setAssets(serverAssets);
          saveCachedAssets(animationId, serverAssets);
        } catch {}
      }
    },
    [animationId, updateAssets],
  );

  // ── Rename asset ──────────────────────────────────────────────────────
  const renameAsset = useCallback(
    async (type, id, newName) => {
      updateAssets((prev) => ({
        ...prev,
        [type]: prev[type].map((a) =>
          a.assetId === id ? { ...a, name: newName } : a,
        ),
      }));
      try {
        await renameAssetService(animationId, id, newName);
      } catch {
        try {
          const serverAssets = await fetchAssets(animationId);
          setAssets(serverAssets);
          saveCachedAssets(animationId, serverAssets);
        } catch {}
      }
    },
    [animationId, updateAssets],
  );

  // ── Recording ─────────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1,
        },
      });

      const audioCtx = new AudioContext({ sampleRate: 48000 });
      const source = audioCtx.createMediaStreamSource(stream);
      const highPass = audioCtx.createBiquadFilter();
      highPass.type = "highpass";
      highPass.frequency.value = 90;
      highPass.Q.value = 0.707;
      const lowPass = audioCtx.createBiquadFilter();
      lowPass.type = "lowpass";
      lowPass.frequency.value = 10000;
      lowPass.Q.value = 0.707;
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = 1.1;
      const dest = audioCtx.createMediaStreamDestination();

      let rnnoiseNode = null;
      try {
        await audioCtx.audioWorklet.addModule("/rnnoise-worklet.js");
        const wasmResponse = await fetch(
          "/node_modules/@jitsi/rnnoise-wasm/dist/rnnoise.wasm",
        );
        if (!wasmResponse.ok) throw new Error("Failed to fetch rnnoise.wasm");
        const wasmBytes = await wasmResponse.arrayBuffer();
        rnnoiseNode = new AudioWorkletNode(audioCtx, "rnnoise-processor", {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [1],
        });
        await new Promise((resolve, reject) => {
          rnnoiseNode.port.onmessage = (e) => {
            if (e.data?.type === "ready") resolve();
            if (e.data?.type === "error") reject(new Error(e.data.message));
          };
          rnnoiseNode.port.postMessage({ type: "init", wasmBytes }, [
            wasmBytes,
          ]);
        });
        source.connect(highPass);
        highPass.connect(rnnoiseNode);
        rnnoiseNode.connect(lowPass);
        lowPass.connect(gainNode);
      } catch {
        rnnoiseNode = null;
        source.connect(highPass);
        highPass.connect(lowPass);
        lowPass.connect(gainNode);
      }

      gainNode.connect(dest);

      const preferredMimeType = MediaRecorder.isTypeSupported(
        "audio/webm;codecs=opus",
      )
        ? "audio/webm;codecs=opus"
        : "audio/webm";

      const recorder = new MediaRecorder(dest.stream, {
        mimeType: preferredMimeType,
        audioBitsPerSecond: 192000,
      });
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = async () => {
        clearInterval(intervalRef.current);

        // Cleanup audio graph
        if (rnnoiseNode) {
          try {
            rnnoiseNode.disconnect();
          } catch {}
          try {
            rnnoiseNode.port.postMessage({ type: "destroy" });
          } catch {}
        }
        try {
          source.disconnect();
        } catch {}
        try {
          highPass.disconnect();
        } catch {}
        try {
          lowPass.disconnect();
        } catch {}
        try {
          gainNode.disconnect();
        } catch {}
        await safelyCloseAudioContext(audioCtx);
        stream.getTracks().forEach((t) => t.stop());

        // Build File from recorded blob
        const blob = new Blob(chunks, { type: "audio/webm" });
        const defaultName = `recording_${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[T:]/g, "-")}`;
        const file = new File([blob], `${defaultName}.webm`, {
          type: "audio/webm",
        });

        // Stage for naming — shows rename input in PresetsPanel before uploading
        await stageAudioFile(file);

        setRecording(false);
        setRecordingTime(0);
        mediaRecorderRef.current = null;
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording("recording");
      setRecordingTime(0);
      intervalRef.current = setInterval(
        () => setRecordingTime((t) => t + 1),
        1000,
      );
    } catch {}
  }, [stageAudioFile]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      clearInterval(intervalRef.current);
      setRecording("paused");
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      setRecording("recording");
      intervalRef.current = setInterval(
        () => setRecordingTime((t) => t + 1),
        1000,
      );
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      clearInterval(intervalRef.current);
      mediaRecorderRef.current.stop();
    }
  }, []);

  const trimAssetFile = useCallback(
    async (type, assetId, payload) => {
      setTrimmingId(assetId);
      try {
        const updated = await trimAsset(animationId, assetId, payload);
        // Update assets in state + local cache
        updateAssets((prev) => {
          const list = prev[type] ?? [];
          if (payload.saveOption === "saveAs") {
            // Add new asset to list
            return { ...prev, [type]: [updated, ...list] };
          } else {
            // Replace existing asset
            return {
              ...prev,
              [type]: list.map((a) => (a.assetId === assetId ? updated : a)),
            };
          }
        });
      } catch (err) {
        // surface error however your hook handles errors
        console.error("Trim failed:", err.message);
      } finally {
        setTrimmingId(null);
      }
    },
    [animationId, updateAssets],
  );

  return {
    assets,
    uploading,
    loadingAssets,
    pendingUpload,
    recording,
    recordingTime,
    stageAudioFile,
    stageImageFile,
    stageVideoFile,
    confirmUpload,
    cancelPendingUpload,
    deleteAsset,
    renameAsset,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    trimAssetFile,
    trimmingId,
  };
};

export default useAnimationAssets;

